import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
    Alert,
    Box,
    Button,
    Center,
    CircularProgress,
    Flex,
    Heading,
    HStack,
    Image,
    Link,
    PinInput,
    PinInputField,
} from '@indicina1/components-library';
import { MouseEvent, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import logo from '../component/icon/logo.png'
import config from '../config/config';
import Decide from '../services/decide';
import { DecideServiceInterface, IConfirmSignup } from '../types/decide';

interface ConfirmSignupProps {
    verificationId: string | null;
    slug: string | null;
    userEmail: string | null;
    setClickVerify: React.Dispatch<React.SetStateAction<boolean>>
}


export default function ConfirmSignup({ verificationId, slug, userEmail, setClickVerify }: ConfirmSignupProps): JSX.Element {

    const decideService: DecideServiceInterface = new Decide(
        window?.decideUrl || config.base_url
    );
    const { watch, control } = useForm({
        defaultValues: {
            otp: '',
        },
    });

    const otp = watch('otp');

    const otpInput = [1, 2, 3, 4, 5];

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>('');
    const [success, setSuccess] = useState<boolean>(false);
    const [resendOtpSuccess, setResendOtpSuccess] = useState<boolean>(false);

    const sendOtp = async (e:  MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsLoading(true);

        const payload: IConfirmSignup = {
            verificationId: verificationId,
            otp,
        };

        const response = await decideService.confirmSignup(payload);
        setIsLoading(false);
        const responseError = decideService.handleResponseError(response);

        if ((response && !response.data) || !response || responseError) {
            setError(responseError ?? 'There was an error confirming your account . Please, try again.');
            return;
        }
        setClickVerify(true)
        setSuccess(true);
    };

    const sendResendOtp = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsLoading(true);
        const response = await decideService.resendOtp(verificationId);
        setIsLoading(false);
        const responseError = decideService.handleResponseError(response);

        if ((response && !response.data) || !response || responseError) {
            setError(responseError ?? 'There was an error resending your OTP. Please, login again.');
            return;
        }

        setResendOtpSuccess(true);
    };


    return (
        <Box data-testid="login">
            <>
                <Box as="figure" p="20px 20px 20px 47px" w="100%">
                    <Link href='/'>
                        <Image
                            src={logo}
                            alt="Decide Merchant Portal"
                            width={150}
                            height={10}
                            data-testid="logo"
                        />
                    </Link>
                </Box>
                <Center w="100%" mt="43px">
                    {(isLoading) && (
                        <Flex
                            w="100%"
                            h="100%"
                            pos="absolute"
                            bg="rgba(255,255,255,0.5)"
                            zIndex="3"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress isIndeterminate size="100px" thickness="3px" />
                        </Flex>
                    )}
                    {success ? (
                        <Flex textAlign="center">
                            <Flex
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                mt="150px"
                                w="520px"
                            >
                                <Box
                                    fontSize="18px"
                                    fontWeight="500"
                                    color="#8A94A6"
                                >
                                    Email verification was successfully
                                </Box>
                                <Link href={`https://${slug}.decide.indicina.co`}>
                                    <Button
                                        variant="link"
                                        mr="28px"
                                        fontWeight="500"
                                        mt="30px"
                                        color="#057BEB"
                                        data-testid="otp-back-to-login"
                                    >
                                        <ChevronLeftIcon
                                            fontSize="24px"
                                            cursor="pointer"
                                            fontWeight="700"
                                        />
                                        Login
                                    </Button>
                                </Link>
                            </Flex>
                        </Flex>
                    ) : (
                        <Flex flexDirection="column">
                            <Flex flexDirection="column" justify="center" align="center" w="520px">
                                <Flex flexDirection="column" justify="center" align="center" mb="30px">
                                    <Heading
                                        level="h1"
                                        fontWeight="700"
                                        fontSize={'24px'}
                                        color="#323B4B"
                                        mb="20px"
                                    >
                                        Please verify your email
                                    </Heading>
                                    <Box
                                        as="h3"
                                        fontWeight="400"
                                        fontSize="18px"
                                        color="#8A94A6"
                                        textAlign="center"
                                        width="401px"
                                    >
                                        You're almost there! We sent a verification code to {userEmail}.
                                    </Box>
                                    {error && (
                                        <Alert
                                            description={error}
                                            status="error"
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            border="1px solid green.800"
                                            fontWeight="400"
                                            color="#323B4B"
                                            fontFamily="heading"
                                            h="auto"
                                            mt="15px"
                                        />
                                    )}

                                    {resendOtpSuccess && (!error) && (
                                        <Alert
                                            description="OTP was sent successfuly"
                                            status="success"
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            border="1px solid green.800"
                                            fontWeight="400"
                                            color="#323B4B"
                                            fontFamily="heading"
                                            h="auto"
                                            mt="15px"
                                        />
                                    )}
                                </Flex>
                            </Flex>

                            <Flex justify="center" align="center" w="100%">
                                <HStack spacing="12px">
                                    <Controller
                                        control={control}
                                        name="otp"
                                        rules={{ required: true }}
                                        render={({ field: { onChange } }) => (
                                            <PinInput
                                                otp
                                                placeholder=""
                                                onChange={(value) => {
                                                    onChange(value);
                                                }}
                                            >
                                                {otpInput?.map((_, index) => (
                                                    <PinInputField
                                                        h="78px"
                                                        w="78px"
                                                        borderRadius="4px"
                                                        fontSize="36px"
                                                        color="#242F57"
                                                        key={index}
                                                        data-testid="otp"
                                                    />
                                                ))}
                                            </PinInput>
                                        )}
                                    />
                                </HStack>
                            </Flex>
                            <Box textAlign="center" mt="24px"
                                color="#8A94A6"
                            >
                                Didn't get a code?
                                <Button variant="link" ml="8px" onClick={sendResendOtp}>
                                    Resend OTP
                                </Button>
                            </Box>
                            <Button variant="primary" onClick={sendOtp} mt="56px" data-testid="otp-btn">
                                Verify
                            </Button>
                        </Flex>
                    )}
                </Center>
            </>
        </Box>
    );
}

